<script setup lang="ts">
import {
    faChevronDown,
    faChevronUp,
    faMouse,
} from '@fortawesome/pro-regular-svg-icons';
import InformationContainer from '@components/ui/auto-reserve&gift-card&index/InformationContainer.vue';
import SiteFooter from '@components/layout/site-footer';
import VideoPlayer from '@components/ui/auto-reserve&view/VideoPlayer.vue';
import { faAutoReserve } from '@static/customIcons';
import SearchReserveForm from '@components/util/search/SearchReserveForm.vue';
import { ReserveType } from '@utils/itinerary';
import LandingBanners from '@components/ui/index&flight-info/LandingBanners.vue';

useServerHead({
    htmlAttrs: {
        class: 'smooth-scroll',
    },
});
const contentStore = useContentStore();
const route = useRoute();
const { landingPageTitle, landingPageDescription } = storeToRefs(contentStore);

useScriptTag('/js/dezhino.js', () => {}, {
    async: true,
    defer: true,
    type: 'text/javascript',
});

await useAsyncData(async () => {
    await contentStore.getPageData(route.path);
    return true;
});

definePageMeta({
    layout: 'pure',
    whiteLabel: 'mrbilit',
});

useSeoMeta({
    title: landingPageTitle,
    description: landingPageDescription,
    ogTitle: landingPageTitle,
    ogDescription: landingPageDescription,
});
const discounted = ref(false);
const mobile = ref('');
const storageKey = 'get-auto-reserve-mobile';
const isNotifyVisible = ref(false);
const showMoreInformation = ref(false);

function notifyVisibilityHandler() {
    isNotifyVisible.value = window.scrollY !== 0;
}

const layoutStore = useLayoutStore();
const { autoReserveInProcessCount } = storeToRefs(layoutStore);
const i18n = useI18n();
const form = useTripSearchForm(false, ReserveType.Flight);
const formAlert = computed(() =>
    autoReserveInProcessCount.value
        ? {
              title: `${autoReserveInProcessCount.value} سفر در حال رهگیری برای رزرو خودکار`,
              href: '/panel/auto-reserve',
          }
        : undefined
);
onMounted(() => {
    const mobileCached = localStorage.getItem(storageKey);
    useContentStore().loadHomepageMessages();
    layoutStore.getAutoReserveInProcessCount();
    if (mobileCached) {
        mobile.value = mobileCached;
        discounted.value = true;
    }
    window.addEventListener('scroll', notifyVisibilityHandler);
});

onBeforeUnmount(() =>
    window.removeEventListener('scroll', notifyVisibilityHandler)
);

const guides = [
    {
        title: 'موجود شد رزرو کن! را انتخاب کنید.',
        description:
            'اگر ظرفیت پرواز مد نظرتان تکمیل باشد. در این حالت گزینه "موجود شد رزرو کن" را انتخاب کنید.',
        color: 'var(--yellow-300)',
    },
    {
        title: 'سطح قیمت بلیط را انتخاب کنید.',
        description:
            'سقف قیمتی که تمایل دارید بلیط رزرو شود انتخاب کنید. در صورتی که قیمت بلیط موجود شده کمتر از سقف باشد، باقیمانده مبلغ در حساب کاربری شما خواهد ماند و تنها مبلغ بلیط به همراه هزینه رزرو خودکار (۳۰۰ هزارتومان هر صورت حساب) از اعتبار شما کسر می‌شود.',
        color: 'var(--green-300)',
    },
    {
        title: 'پرواز رو انتخاب کنید.',
        description:
            'شما میتوانید هر تعداد پروازی که در روز تعیین شده تکمیل ظرفیت هست را انتخاب کنید.',
        color: 'var(--red-300)',
    },
    {
        title: 'مشخصات خود را وارد کنید.',
        description: 'مشخصات مسافران را وارد کنید',
        color: 'var(--cyan-300)',
    },
    {
        title: 'اعتبار خود را شارژ کنید.',
        description:
            'نیاز است حساب کاربری خود را به اندازه سقف تعیین شده + ۳۰۰ هزارتومان هزینه رزرو خودکار شارژ کنید. در صورت صادر نشدن بلیط کل مبلغ به حساب شما بر می‌گردد.',
        color: 'var(--purple-300)',
    },
];
</script>

<template>
    <div>
        <header class="auto-reserve-header">
            <div class="header-options-container">
                <mr-button
                    variant="transparent"
                    link="nuxt"
                    href="/plane-ticket"
                >
                    <mr-typography
                        size="display3"
                        weight="700"
                        color="var(--blue-400)"
                    >
                        مِستربلیط
                    </mr-typography>
                </mr-button>
                <mr-button
                    variant="transparent"
                    href="#search"
                    :right-icon="{
                        icon: faAutoReserve,
                        color: 'var(--blue-400)',
                    }"
                    link="nuxt"
                >
                    <mr-typography
                        type="h1"
                        weight="700"
                        color="var(--blue-400)"
                    >
                        جستجو پرواز
                    </mr-typography>
                </mr-button>
            </div>
        </header>
        <div class="page-wrapper">
            <section class="header">
                <div class="headers-details">
                    <div class="details">
                        <mr-typography
                            :size="{ desktop: 'display1', mobile: 'display3' }"
                            :weight="{ desktop: '700', mobile: '900' }"
                            color="var(--cool-gray-900)"
                        >
                            بلیط هواپیما نیست؟ رزرو خودکار که هست
                        </mr-typography>
                        <mr-typography
                            size="x-large"
                            color="var(--cool-gray-800)"
                        >
                            رزرو خودکار هواپیما را فعال کنید تا در لحظه‌‌ی موجود
                            شدن ظرفیت خالی (به دلیل کنسلی یا افزایش ظرفیت)، آن
                            بلیط برای شما خریداری شود.
                        </mr-typography>
                        <div class="navigate-bottom">
                            <mr-button
                                variant="transparent"
                                href="#information"
                                link="nuxt"
                                :right-icon="{
                                    icon: faMouse,
                                    color: 'var(--cool-gray-900)',
                                }"
                            >
                                <mr-typography
                                    type="span"
                                    size="x-large"
                                    weight="700"
                                    color="var(--cool-gray-900)"
                                >
                                    نحوه‌ی فعالسازی رزرو خودکار در مِستربلیط
                                </mr-typography>
                            </mr-button>
                        </div>
                    </div>
                    <div class="player-container">
                        <VideoPlayer class="player" />
                    </div>
                </div>
                <mr-typography
                    id="search"
                    size="large"
                    weight="700"
                    color="var(--cool-gray-900)"
                    class="search-box-title"
                >
                    جستجوی بلیط هواپیما
                </mr-typography>
                <SearchReserveForm
                    v-bind="form"
                    class="flight-search-form"
                    :form-alert
                    :origin-title="i18n.t('SearchFlightForm.origin')"
                    :destination-title="i18n.t('SearchFlightForm.destination')"
                    :reserve-type="ReserveType.Flight"
                />
            </section>
            <mr-typography
                id="information"
                size="x-large"
                weight="700"
                color="var(--coo-gray-900)"
                class="guides-headers"
            >
                نحوه فعال‌سازی رزرو خودکار
            </mr-typography>
            <section class="guid-section">
                <div class="guides">
                    <div class="guid">
                        <div class="guid-header">
                            <div class="icon-container">
                                <mr-typography
                                    size="x-large"
                                    weight="700"
                                    color="white"
                                >
                                    1
                                </mr-typography>
                            </div>
                            <mr-typography
                                size="x-large"
                                weight="700"
                                color="var(--gray-900)"
                            >
                                مبدأ و مقصد سفر را جستجو کنید.
                            </mr-typography>
                        </div>
                        <mr-typography
                            size="large"
                            weight="500"
                            color="var(--gray-700)"
                        >
                            1. در صفحه
                            <mr-button
                                variant="transparent"
                                link
                                href="/plane-ticket"
                                target="_blank"
                                class="link"
                            >
                                بلیط هواپیما
                            </mr-button>
                            مبدأ، مقصد و زمان سفر را مشخص و جستجو کنید.
                        </mr-typography>
                    </div>
                    <div
                        v-for="(guid, index) in guides"
                        :key="guid.title"
                        class="guid"
                    >
                        <div class="guid-header">
                            <div
                                class="icon-container"
                                :style="{ background: guid.color }"
                            >
                                <mr-typography
                                    size="x-large"
                                    weight="700"
                                    color="white"
                                >
                                    {{ index + 2 }}
                                </mr-typography>
                            </div>
                            <mr-typography
                                size="x-large"
                                weight="700"
                                color="var(--gray-900)"
                            >
                                {{ guid.title }}
                            </mr-typography>
                        </div>
                        <mr-typography
                            size="large"
                            weight="500"
                            color="var(--gray-700)"
                        >
                            {{ guid.description }}
                        </mr-typography>
                    </div>
                </div>
                <div class="final-step">
                    <mr-typography
                        size="x-large"
                        color="var(--blue-400)"
                        weight="700"
                    >
                        7. منتظر جادوی رزرو خودکار مِستربلیط بمانید
                    </mr-typography>
                    <mr-typography color="var(--cool-gray-800)" weight="500">
                        با رزرو شدن بلیط به شما پیامک رزرو موفق ارسال می‌شود و
                        می‌توانید با آرامش به مسافرت خود بپردازید.
                    </mr-typography>
                </div>
            </section>
            <LandingBanners />
            <div>
                <InformationContainer
                    class="faq-container"
                    :class="{ show: showMoreInformation }"
                    faq-title="سوالات متداول رزرو خودکار"
                    hide-busy-way
                    information-position="bottom"
                />
                <div class="more-info">
                    <mr-button
                        variant="transparent"
                        :left-icon="
                            showMoreInformation ? faChevronUp : faChevronDown
                        "
                        @click="showMoreInformation = !showMoreInformation"
                    >
                        نمایش بیشتر
                    </mr-button>
                </div>
            </div>
            <SiteFooter class="footer" compact />
        </div>
    </div>
</template>

<style scoped lang="scss">
$breakpoint: 1300px;
.auto-reserve-header {
    position: sticky;
    top: 0;
    width: 100vw;
    background-color: white;
    z-index: 9999;
    @include mobile-only($breakpoint) {
        padding: 0 20px 0 24px;
    }
    .header-options-container {
        margin: 0 auto;
        max-width: 1200px;
        height: 70px;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
}
.page-wrapper {
    display: flex;
    flex-direction: column;
    background-color: white;
    align-items: center;
    padding: 40px 120px 0;
    gap: 40px;
    @include mobile-only($breakpoint) {
        padding: 0;
        gap: 40px;
        align-items: normal;
    }

    @include mobile-only {
        gap: 0;
    }

    section {
        max-width: 1200px;
    }

    .header {
        display: flex;
        flex-direction: column;
        padding: 32px 24px;
        border-radius: 16px;
        gap: 4px;
        background-color: usePalette($p-cool-gray, $p-100);
        @include mobile-only {
            gap: 8px;
            padding: 0;
            border-radius: 0;
        }
        .search-box-title {
            scroll-margin-top: 70px;
            @include mobile-only {
                padding: 8px;
                background-color: white;
                margin-bottom: -8px;
                display: flex;
                justify-content: center;
            }
        }
        .flight-search-form {
            flex-grow: 1;
            width: 100%;
            max-width: 100%;
            @include mobile-only {
                min-width: 100px;
                box-shadow: none;
                padding-bottom: 8px;
            }
        }
        .headers-details {
            display: flex;
            align-items: center;
            gap: 40px;
            @include mobile-only($breakpoint) {
                flex-direction: column;
                padding: 32px 20px;
                gap: 16px;
            }
            .player-container {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 700px;
                height: 394px;
                flex-shrink: 0;
                border-radius: 16px;
                @include mobile-only($breakpoint) {
                    width: 100%;
                    flex-shrink: 1;
                    height: 250px;
                }
                .player {
                    height: 100%;
                    width: 100%;
                    @include mobile-only($breakpoint) {
                        max-width: 700px;
                    }
                    @include mobile-only {
                        max-width: 500px;
                    }
                }
            }

            .details {
                flex-grow: 1;
                display: flex;
                flex-direction: column;
                gap: 16px;

                .navigate-bottom {
                    display: flex;
                    align-items: center;
                }
            }
        }
    }
    .guides-headers {
        scroll-margin-top: 70px;
        @include mobile-only {
            padding-bottom: 12px;
            margin-inline: auto;
        }
    }

    .guid-section {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding-block: 8px;
        .guides {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;

            grid-gap: 40px;
            @include mobile-only($breakpoint) {
                padding-inline: 20px;
            }
            @include mobile-only {
                display: flex;
                flex-direction: column;
                gap: 16px;
                padding: 0 20px;
            }
            .guid {
                flex-basis: calc(33.33% - 100px);
                min-width: 373px;
                display: flex;
                flex-direction: column;
                padding: 16px;
                border-radius: 16px;
                gap: 8px;
                background-color: usePalette($p-cool-gray, $p-100);
                @include mobile-only(920px) {
                    flex-grow: 1;
                    flex-basis: 100%;
                    width: 100%;
                    min-width: auto;
                }
                .link {
                    display: inline;
                }
                .guid-header {
                    display: flex;
                    align-items: center;
                    gap: 16px;

                    .icon-container {
                        flex-shrink: 0;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 52px;
                        height: 52px;
                        padding: 8px;
                        border-radius: 999999px;
                        gap: 8px;
                        background-color: usePalette($p-blue, $p-300);
                    }
                }
            }
        }
        .final-step {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 20px;
            gap: 4px;
        }
    }

    .faq-container {
        max-height: 800px;
        overflow: hidden;
        margin-top: -80px;
        @include mobile-only($breakpoint) {
            padding-inline: 20px;
            max-height: 900px;
            margin-top: -40px;
        }
    }

    .show {
        max-height: fit-content !important;
    }

    .more-info {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .download-app {
        padding-block: 0 !important;
        padding-inline: 0 !important;
        background-color: white;
        :deep() {
            .container {
                max-width: 100%;
            }
        }
    }
    .footer {
        flex-grow: 1;
        width: 100%;
        box-shadow: none !important;
        &:deep() {
            .top-row {
                display: none;
            }
        }
    }
}

:global(.sticky) {
    @include mobile-only {
        background: usePalette($p-blue, $p-300) !important;
    }
}
</style>
